import { GamePhase } from '@arena/shared';
import { useRoomBrowser, type ListedRoom } from '../../net/lobby';

/** Seats shown in the listing: humans + bots out of the match size, from the room's metadata. */
export function seatsOf(r: ListedRoom): { players: number; max: number; bots: number } {
  return {
    players: r.metadata?.players ?? r.clients,
    max: r.metadata?.maxPlayers ?? r.maxClients,
    bots: r.metadata?.bots ?? 0,
  };
}

/** A human can join while waiting if a seat is free or a bot can be evicted to free one. */
export function isJoinable(r: ListedRoom): boolean {
  const { players, max, bots } = seatsOf(r);
  return r.metadata?.phase === GamePhase.Waiting && (players < max || bots > 0);
}

/** Joinable rooms first, then the fullest; ties keep the server's order. */
export function sortRooms(rooms: readonly ListedRoom[]): ListedRoom[] {
  return rooms
    .map((room, i) => ({ room, i, open: isJoinable(room), players: seatsOf(room).players }))
    .sort((a, b) => Number(b.open) - Number(a.open) || b.players - a.players || a.i - b.i)
    .map((r) => r.room);
}

const MODE_LABELS: Record<string, string> = { ffa: 'FFA', teams: 'Teams' };

function SeatBar({ players, max }: { players: number; max: number }) {
  return (
    <span className="seat-bar" aria-hidden="true">
      {Array.from({ length: max }, (_, i) => (
        <span key={i} className={i < players ? 'taken' : undefined} />
      ))}
    </span>
  );
}

function MatchRow({
  room,
  onJoin,
  disabled,
}: {
  room: ListedRoom;
  onJoin: (roomId: string) => void;
  disabled: boolean;
}) {
  const { players, max, bots } = seatsOf(room);
  const open = isJoinable(room);
  const mode = room.metadata?.mode ?? 'ffa';
  const host = room.metadata?.hostName;
  return (
    <li className={open ? 'joinable' : 'closed'}>
      <span className="match-name">
        {room.metadata?.name ?? room.roomId}
        {host && <span className="muted match-host"> · {host}</span>}
      </span>
      <span className="match-meta">
        <span className="mode-chip">{MODE_LABELS[mode] ?? mode}</span>
        <span className="match-seats">
          {players}/{max}
          <SeatBar players={players} max={max} />
        </span>
        {bots > 0 && (
          <span className="bot-count">
            <span aria-hidden="true">🤖</span> {bots}
            <span className="sr-only"> {bots === 1 ? 'bot' : 'bots'}</span>
          </span>
        )}
      </span>
      {open ? (
        <button disabled={disabled} onClick={() => onJoin(room.roomId)}>
          Join
        </button>
      ) : (
        <span className="muted match-state">
          {room.metadata?.phase === GamePhase.Waiting ? 'Full' : 'In match'}
        </span>
      )}
    </li>
  );
}

/** Home → Join Lobby → Open matches: the live room list from the `lobby` room. */
export function OpenMatches({
  onJoin,
  onQuickMatch,
  onCreate,
  disabled,
  quickLabel = 'Quick Match',
  createLabel = 'Create Lobby',
}: {
  onJoin: (roomId: string) => void;
  /** The empty state's shortcuts: the same actions as in Play (the lobby is public). */
  onQuickMatch: () => void;
  onCreate: () => void;
  disabled: boolean;
  /** "Connecting…" on the shortcut that started the pending connection. */
  quickLabel?: string;
  createLabel?: string;
}) {
  const { rooms, online } = useRoomBrowser();

  return (
    <section className="browser" aria-labelledby="open-matches-title">
      <h3 id="open-matches-title" className="browser-title">
        Open matches{online && <span className="muted"> · {rooms.length}</span>}
      </h3>
      {!online ? (
        <p className="muted">Server offline, can't list open matches.</p>
      ) : rooms.length === 0 ? (
        <div className="browser-empty">
          <p className="muted">No open matches right now.</p>
          <div className="row">
            <button disabled={disabled} onClick={onQuickMatch}>
              {quickLabel}
            </button>
            <button disabled={disabled} onClick={onCreate}>
              {createLabel}
            </button>
          </div>
        </div>
      ) : (
        <ul>
          {sortRooms(rooms).map((r) => (
            <MatchRow key={r.roomId} room={r} onJoin={onJoin} disabled={disabled} />
          ))}
        </ul>
      )}
    </section>
  );
}
